/**
 * Work Order creation — 4-step sheet:
 * 1. Basic (project, title, vendor picker via /vendors/search)
 * 2. Contract type selector (6 cards)
 * 3. Commercial terms (type-specific fields, milestones/deliverables)
 * 4. Review → Save Draft / Submit for approval
 */
import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import {
  Ruler, Layers, Wrench, FileText, Truck, RefreshCw, Search, ChevronRight, ChevronLeft,
} from 'lucide-react';
import { apiGet, apiPost } from '../../lib/api';
import { formatINR } from '../../lib/format';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '../../components/ui/sheet';
import { CONTRACT_TYPES } from './WorkOrders';

const TYPE_ICONS = {
  measured_works: Ruler, lump_sum: Layers, supply_erection: Wrench,
  consultancy: FileText, equipment_hire: Truck, amc_recurring: RefreshCw,
};
const inp = 'w-full text-xs border border-slate-200 rounded-sm px-2 py-1.5 focus:outline-none focus:border-[#3b4872]';

export default function WorkOrderCreate({ onClose }) {
  const [step, setStep] = useState(1);
  const [projects, setProjects] = useState([]);
  const [saving, setSaving] = useState(false);
  const [f, setF] = useState({
    project_id: '', project_name: '', title: '',
    vendor_id: '', vendor_name: '', vendor_code: '', tds_section: '',
    contract_type: '',
    scope_description: '', start_date: '', end_date: '',
    contract_value: '', rate_basis: 'item_rate', payment_terms: '',
    retention_pct: '5', dlp_months: '0', ld_clause_pct: '0',
    mobilisation_advance: '0', advance_recovery_pct: '0',
    security_deposit_amount: '0', security_deposit_mode: '',
    equipment_description: '', min_guarantee_hours: '0', hire_rate_per_hour: '0',
    idle_rate_per_hour: '0', operator_included: false, fuel_scope: 'vendor',
    amc_monthly_value: '0', amc_period_months: '12', sla_terms: '',
    notes: '',
  });
  const [milestones, setMilestones] = useState([]);
  const [deliverables, setDeliverables] = useState([]);

  useEffect(() => {
    apiGet('/projects').then((d) => setProjects(Array.isArray(d) ? d : (d?.items || []))).catch(() => {});
  }, []);

  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const showMilestones = ['lump_sum', 'supply_erection'].includes(f.contract_type);
  const showDeliverables = f.contract_type === 'consultancy';
  const isHire = f.contract_type === 'equipment_hire';
  const isAmc = f.contract_type === 'amc_recurring';

  const canNext = () => {
    if (step === 1) return !!f.title.trim();
    if (step === 2) return !!f.contract_type;
    if (step === 3) return Number(f.contract_value) > 0 || isAmc;
    return true;
  };

  const buildPayload = () => ({
    ...f,
    contract_value: Number(f.contract_value) || (isAmc ? Number(f.amc_monthly_value) * Number(f.amc_period_months) : 0),
    retention_pct: Number(f.retention_pct), dlp_months: Number(f.dlp_months),
    ld_clause_pct: Number(f.ld_clause_pct), mobilisation_advance: Number(f.mobilisation_advance),
    advance_recovery_pct: Number(f.advance_recovery_pct),
    security_deposit_amount: Number(f.security_deposit_amount),
    min_guarantee_hours: Number(f.min_guarantee_hours), hire_rate_per_hour: Number(f.hire_rate_per_hour),
    idle_rate_per_hour: Number(f.idle_rate_per_hour),
    amc_monthly_value: Number(f.amc_monthly_value), amc_period_months: Number(f.amc_period_months),
    milestones: showMilestones ? milestones.filter((m) => m.title).map((m) => ({ ...m, billing_pct: Number(m.billing_pct) || 0 })) : [],
    deliverables: showDeliverables ? deliverables.filter((d) => d.title).map((d) => ({ ...d, billing_pct: Number(d.billing_pct) || 0 })) : [],
  });

  const save = async (submit) => {
    setSaving(true);
    try {
      const wo = await apiPost('/work-orders', buildPayload());
      if (submit) await apiPost(`/work-orders/${wo.id}/submit`);
      toast.success(`${wo.wo_no} ${submit ? 'submitted for approval' : 'saved as draft'}`);
      onClose(true);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to create work order'); }
    finally { setSaving(false); }
  };

  return (
    <Sheet open onOpenChange={(o) => !o && onClose(false)}>
      <SheetContent className="w-full sm:max-w-xl overflow-y-auto" data-testid="wo-create-sheet">
        <SheetHeader>
          <SheetTitle className="font-display">New Work Order</SheetTitle>
          <SheetDescription>Step {step} of 4 — {['Basics', 'Contract type', 'Commercial terms', 'Review'][step - 1]}</SheetDescription>
        </SheetHeader>

        {/* Step indicator */}
        <div className="flex gap-1 mt-3">
          {[1, 2, 3, 4].map((s) => (
            <div key={s} className={`h-1 flex-1 rounded-full ${s <= step ? 'bg-[#3b4872]' : 'bg-slate-200'}`} />
          ))}
        </div>

        <div className="mt-4 space-y-3">
          {step === 1 && (
            <>
              <label className="text-[10px] text-slate-500 font-display uppercase tracking-wide">Project
                <select value={f.project_id} onChange={(e) => {
                  const p = projects.find((x) => x.id === e.target.value);
                  setF((prev) => ({ ...prev, project_id: e.target.value, project_name: p?.name || '' }));
                }} className={`${inp} mt-1`} data-testid="wo-form-project">
                  <option value="">— Select project (optional) —</option>
                  {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              </label>
              <label className="text-[10px] text-slate-500 font-display uppercase tracking-wide">Title *
                <input value={f.title} onChange={(e) => set('title', e.target.value)} placeholder="e.g. PEB Erection at Sinnar Plant Phase 1" className={`${inp} mt-1`} data-testid="wo-form-title" />
              </label>
              <VendorPicker
                value={f}
                onPick={(v) => setF((prev) => ({ ...prev, vendor_id: v.id, vendor_name: v.name, vendor_code: v.vendor_code, tds_section: v.tds_section || '' }))}
                onClear={() => setF((prev) => ({ ...prev, vendor_id: '', vendor_name: '', vendor_code: '', tds_section: '' }))}
              />
              <label className="text-[10px] text-slate-500 font-display uppercase tracking-wide">Scope description
                <textarea value={f.scope_description} onChange={(e) => set('scope_description', e.target.value)} className={`${inp} mt-1 h-20`} data-testid="wo-form-scope" />
              </label>
              <div className="grid grid-cols-2 gap-2">
                <label className="text-[10px] text-slate-500 font-display uppercase tracking-wide">Start date
                  <input type="date" value={f.start_date} onChange={(e) => set('start_date', e.target.value)} className={`${inp} mt-1`} /></label>
                <label className="text-[10px] text-slate-500 font-display uppercase tracking-wide">Planned completion
                  <input type="date" value={f.end_date} onChange={(e) => set('end_date', e.target.value)} className={`${inp} mt-1`} /></label>
              </div>
            </>
          )}

          {step === 2 && (
            <div className="grid grid-cols-2 gap-2" data-testid="wo-type-cards">
              {CONTRACT_TYPES.map((c) => {
                const Icon = TYPE_ICONS[c.id];
                const active = f.contract_type === c.id;
                return (
                  <button key={c.id} onClick={() => set('contract_type', c.id)}
                    className={`text-left p-3 border rounded-sm transition-colors ${active ? 'border-[#3b4872] bg-[#3b4872]/5 ring-1 ring-[#3b4872]' : 'border-slate-200 hover:border-slate-400 bg-white'}`}
                    data-testid={`wo-type-${c.id}`}>
                    <Icon size={18} className={active ? 'text-[#3b4872]' : 'text-slate-400'} />
                    <div className="font-display font-bold text-xs mt-1.5">{c.label}</div>
                    <div className="text-[10px] text-slate-500 mt-0.5">{c.desc}</div>
                  </button>
                );
              })}
            </div>
          )}

          {step === 3 && (
            <>
              <div className="grid grid-cols-2 gap-2">
                <label className="text-[10px] text-slate-500 font-display uppercase tracking-wide">{isAmc ? 'Total contract value (optional)' : 'Contract value (₹) *'}
                  <input type="number" value={f.contract_value} onChange={(e) => set('contract_value', e.target.value)} className={`${inp} mt-1`} data-testid="wo-form-value" /></label>
                <label className="text-[10px] text-slate-500 font-display uppercase tracking-wide">Rate basis
                  <select value={f.rate_basis} onChange={(e) => set('rate_basis', e.target.value)} className={`${inp} mt-1`}>
                    {['item_rate', 'lump_sum', 'hourly', 'daily', 'monthly'].map((r) => <option key={r} value={r}>{r.replace('_', ' ')}</option>)}
                  </select></label>
                <label className="text-[10px] text-slate-500 font-display uppercase tracking-wide">Payment terms
                  <input value={f.payment_terms} onChange={(e) => set('payment_terms', e.target.value)} placeholder="30 days from invoice" className={`${inp} mt-1`} /></label>
                <label className="text-[10px] text-slate-500 font-display uppercase tracking-wide">Retention %
                  <input type="number" value={f.retention_pct} onChange={(e) => set('retention_pct', e.target.value)} className={`${inp} mt-1`} data-testid="wo-form-retention" /></label>
                <label className="text-[10px] text-slate-500 font-display uppercase tracking-wide">DLP (months)
                  <input type="number" value={f.dlp_months} onChange={(e) => set('dlp_months', e.target.value)} className={`${inp} mt-1`} /></label>
                <label className="text-[10px] text-slate-500 font-display uppercase tracking-wide">LD % per week delay
                  <input type="number" value={f.ld_clause_pct} onChange={(e) => set('ld_clause_pct', e.target.value)} className={`${inp} mt-1`} /></label>
                <label className="text-[10px] text-slate-500 font-display uppercase tracking-wide">Mobilisation advance (₹)
                  <input type="number" value={f.mobilisation_advance} onChange={(e) => set('mobilisation_advance', e.target.value)} className={`${inp} mt-1`} /></label>
                <label className="text-[10px] text-slate-500 font-display uppercase tracking-wide">Advance recovery % per bill
                  <input type="number" value={f.advance_recovery_pct} onChange={(e) => set('advance_recovery_pct', e.target.value)} className={`${inp} mt-1`} /></label>
                <label className="text-[10px] text-slate-500 font-display uppercase tracking-wide">Security deposit (₹)
                  <input type="number" value={f.security_deposit_amount} onChange={(e) => set('security_deposit_amount', e.target.value)} className={`${inp} mt-1`} /></label>
                <label className="text-[10px] text-slate-500 font-display uppercase tracking-wide">SD mode
                  <select value={f.security_deposit_mode} onChange={(e) => set('security_deposit_mode', e.target.value)} className={`${inp} mt-1`}>
                    <option value="">—</option><option value="cash">Cash</option><option value="bg">Bank Guarantee</option><option value="dd">DD</option>
                  </select></label>
              </div>

              {isHire && (
                <div className="border border-slate-200 rounded-sm p-3 space-y-2 bg-slate-50">
                  <div className="kpi-overline">Equipment Hire Terms</div>
                  <input placeholder="Equipment description (e.g. 50T crane with operator)" value={f.equipment_description} onChange={(e) => set('equipment_description', e.target.value)} className={inp} />
                  <div className="grid grid-cols-3 gap-2">
                    <label className="text-[10px] text-slate-500">Min guarantee hrs/month<input type="number" value={f.min_guarantee_hours} onChange={(e) => set('min_guarantee_hours', e.target.value)} className={`${inp} mt-0.5`} /></label>
                    <label className="text-[10px] text-slate-500">Hire rate ₹/hr<input type="number" value={f.hire_rate_per_hour} onChange={(e) => set('hire_rate_per_hour', e.target.value)} className={`${inp} mt-0.5`} /></label>
                    <label className="text-[10px] text-slate-500">Idle rate ₹/hr<input type="number" value={f.idle_rate_per_hour} onChange={(e) => set('idle_rate_per_hour', e.target.value)} className={`${inp} mt-0.5`} /></label>
                  </div>
                  <div className="flex gap-4 text-xs">
                    <label className="flex items-center gap-1.5"><input type="checkbox" checked={f.operator_included} onChange={(e) => set('operator_included', e.target.checked)} /> Operator included</label>
                    <label className="flex items-center gap-1.5">Fuel scope:
                      <select value={f.fuel_scope} onChange={(e) => set('fuel_scope', e.target.value)} className="text-xs border border-slate-200 rounded-sm px-1.5 py-1">
                        <option value="vendor">Vendor</option><option value="client">Client (us)</option>
                      </select></label>
                  </div>
                </div>
              )}

              {isAmc && (
                <div className="border border-slate-200 rounded-sm p-3 space-y-2 bg-slate-50">
                  <div className="kpi-overline">AMC Terms</div>
                  <div className="grid grid-cols-2 gap-2">
                    <label className="text-[10px] text-slate-500">Monthly value ₹<input type="number" value={f.amc_monthly_value} onChange={(e) => set('amc_monthly_value', e.target.value)} className={`${inp} mt-0.5`} data-testid="wo-form-amc-monthly" /></label>
                    <label className="text-[10px] text-slate-500">Period (months)<input type="number" value={f.amc_period_months} onChange={(e) => set('amc_period_months', e.target.value)} className={`${inp} mt-0.5`} /></label>
                  </div>
                  <textarea placeholder="SLA terms (e.g. 2 visits/month, 4hr response)" value={f.sla_terms} onChange={(e) => set('sla_terms', e.target.value)} className={`${inp} h-14`} />
                </div>
              )}

              {showMilestones && (
                <RowsEditor title="Milestones" rows={milestones} setRows={setMilestones}
                  fields={[['title', 'Milestone title'], ['billing_pct', '% billing'], ['planned_date', 'date']]} testid="wo-milestones" />
              )}
              {showDeliverables && (
                <RowsEditor title="Deliverables" rows={deliverables} setRows={setDeliverables}
                  fields={[['title', 'Deliverable title'], ['billing_pct', '% billing']]} testid="wo-deliverables" />
              )}
            </>
          )}

          {step === 4 && (
            <div className="space-y-2 text-sm" data-testid="wo-review">
              <ReviewRow label="Title" value={f.title} />
              <ReviewRow label="Project" value={f.project_name || '—'} />
              <ReviewRow label="Vendor" value={f.vendor_name ? `${f.vendor_code} · ${f.vendor_name}` : '— (none picked)'} />
              <ReviewRow label="Contract type" value={CONTRACT_TYPES.find((c) => c.id === f.contract_type)?.label} />
              <ReviewRow label="Value" value={formatINR(Number(f.contract_value) || Number(f.amc_monthly_value) * Number(f.amc_period_months))} />
              <ReviewRow label="Retention" value={`${f.retention_pct}% · DLP ${f.dlp_months} months · LD ${f.ld_clause_pct}%/wk`} />
              <ReviewRow label="Advance" value={`${formatINR(Number(f.mobilisation_advance))} (recovery ${f.advance_recovery_pct}%/bill)`} />
              <ReviewRow label="TDS" value={f.tds_section || 'none'} />
              {showMilestones && <ReviewRow label="Milestones" value={`${milestones.filter((m) => m.title).length} defined`} />}
              {showDeliverables && <ReviewRow label="Deliverables" value={`${deliverables.filter((d) => d.title).length} defined`} />}
            </div>
          )}
        </div>

        {/* Footer nav */}
        <div className="flex items-center justify-between gap-2 mt-5 pt-3 border-t border-slate-100">
          <button onClick={() => (step === 1 ? onClose(false) : setStep(step - 1))} className="flex items-center gap-1 px-3 py-2 text-xs text-slate-600 hover:bg-slate-100 rounded-sm font-display">
            <ChevronLeft size={12} /> {step === 1 ? 'Cancel' : 'Back'}
          </button>
          {step < 4 ? (
            <button onClick={() => setStep(step + 1)} disabled={!canNext()}
              className="flex items-center gap-1 px-4 py-2 text-xs bg-[#3b4872] hover:bg-[#2d3856] text-white rounded-sm font-display font-bold uppercase tracking-wider disabled:opacity-40"
              data-testid="wo-form-next">
              Next <ChevronRight size={12} />
            </button>
          ) : (
            <div className="flex gap-2">
              <button onClick={() => save(false)} disabled={saving} className="px-3 py-2 text-xs border border-[#3b4872] text-[#3b4872] rounded-sm font-display font-bold uppercase tracking-wider disabled:opacity-50" data-testid="wo-form-save-draft">
                Save Draft
              </button>
              <button onClick={() => save(true)} disabled={saving} className="px-4 py-2 text-xs bg-[#3b4872] hover:bg-[#2d3856] text-white rounded-sm font-display font-bold uppercase tracking-wider disabled:opacity-50" data-testid="wo-form-submit">
                {saving ? 'Saving…' : 'Submit for Approval'}
              </button>
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}

function VendorPicker({ value, onPick, onClear }) {
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const timer = useRef(null);

  const searchVendors = (text) => {
    setQ(text);
    clearTimeout(timer.current);
    if (!text) { setResults([]); setOpen(false); return; }
    timer.current = setTimeout(async () => {
      try {
        const rows = await apiGet('/vendors/search', { q: text });
        setResults(rows || []); setOpen(true);
      } catch {
        setResults([]);
        toast.error('Could not load vendors — please try again');
      }
    }, 250);
  };

  if (value.vendor_id) {
    return (
      <div className="flex items-center justify-between border border-emerald-200 bg-emerald-50 rounded-sm px-2.5 py-2" data-testid="wo-vendor-selected">
        <div className="text-xs"><span className="font-mono text-slate-500">{value.vendor_code}</span> <span className="font-display font-bold">{value.vendor_name}</span> <span className="text-slate-500">· TDS {value.tds_section || '—'}</span></div>
        <button onClick={onClear} className="text-[11px] text-slate-500 hover:text-red-600">Change</button>
      </div>
    );
  }
  return (
    <div className="relative">
      <label className="text-[10px] text-slate-500 font-display uppercase tracking-wide">Vendor (from Vendor Master)</label>
      <div className="relative mt-1">
        <Search size={13} className="absolute left-2 top-2 text-slate-400" />
        <input value={q} onChange={(e) => searchVendors(e.target.value)} placeholder="Type to search vendors…" className={`${inp} pl-7`} data-testid="wo-vendor-search" />
      </div>
      {open && results.length > 0 && (
        <div className="absolute z-20 mt-1 w-full bg-white border border-slate-200 rounded-sm shadow-lg max-h-48 overflow-y-auto">
          {results.map((v) => (
            <button key={v.id} onClick={() => { onPick(v); setOpen(false); setQ(''); }}
              className="w-full text-left px-2.5 py-2 text-xs hover:bg-slate-50 border-b border-slate-100 last:border-0"
              data-testid={`wo-vendor-option-${v.vendor_code}`}>
              <span className="font-mono text-slate-500">{v.vendor_code}</span> <span className="font-display font-semibold">{v.name}</span>
              <span className={`ml-2 px-1 py-0.5 text-[9px] rounded-sm uppercase ${v.empanelment_status === 'approved' ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'}`}>{v.empanelment_status}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function RowsEditor({ title, rows, setRows, fields, testid }) {
  return (
    <div className="border border-slate-200 rounded-sm p-3 space-y-2 bg-slate-50" data-testid={testid}>
      <div className="kpi-overline">{title}</div>
      {rows.map((r, i) => (
        <div key={i} className="flex gap-2">
          {fields.map(([k, ph]) => (
            <input key={k} type={k === 'billing_pct' ? 'number' : k.includes('date') ? 'date' : 'text'}
              placeholder={ph} value={r[k] || ''}
              onChange={(e) => setRows(rows.map((x, j) => (j === i ? { ...x, [k]: e.target.value } : x)))}
              className={`${inp} ${k === 'title' ? 'flex-1' : 'w-28'}`} />
          ))}
          <button onClick={() => setRows(rows.filter((_, j) => j !== i))} className="text-xs text-red-500 px-1">✕</button>
        </div>
      ))}
      <button onClick={() => setRows([...rows, {}])} className="text-xs text-[#3b4872] font-display font-bold" data-testid={`${testid}-add`}>+ Add {title.slice(0, -1).toLowerCase()}</button>
      {rows.length > 0 && (
        <div className="text-[10px] text-slate-500">Total billing: {rows.reduce((s, r) => s + (Number(r.billing_pct) || 0), 0)}%</div>
      )}
    </div>
  );
}

const ReviewRow = ({ label, value }) => (
  <div className="flex justify-between border-b border-slate-100 pb-1.5">
    <span className="text-xs text-slate-500 font-display uppercase tracking-wide">{label}</span>
    <span className="text-xs font-display font-semibold text-right">{value}</span>
  </div>
);
